"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { cn, tw } from "@/lib/utils";
import { isContentEmpty, safeRenderToReactElement } from "@/ui/admin/tiptap-utils";
import type { HeroSlide } from "@/ui/yns-components/components/hero-section/hero-section";
import type { ComponentBuilderConfigParams } from "@/ui/yns-components/components/types";
import { YnsLink } from "@/ui/yns-link";
import { RichTextEditorLazy } from "../rich-text/rich-text-editor-lazy";
import type { HeroSectionStoreBuilderConfigFactory } from "./config";

const TIME = 5000;

declare module "csstype" {
	interface Properties {
		"--time"?: `${number}ms`;
	}
}

export const HeroSectionInner = ({
	slides,
	size = "fixed-width",
	theme,
	isBuilder,
}: {
	slides: HeroSlide[];
	size?: "full-width" | "fixed-width";
	isBuilder: boolean;
	/**
	 * @deprecated
	 */
	theme?: ComponentBuilderConfigParams<typeof HeroSectionStoreBuilderConfigFactory>["theme"];
}) => {
	const [currentSlide, setCurrentSlide] = useState(0);

	// undefined = not started
	// null = manually stopped (on click)
	// number = running
	const intervalRef = useRef<ReturnType<typeof setInterval> | null | undefined>(undefined);

	useEffect(() => {
		intervalRef.current = setInterval(() => {
			setCurrentSlide((prev) => (prev + 1) % slides.length);
		}, TIME);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [slides.length]);

	return (
		<div>
			{slides.map((slide, idx) => {
				const content = isContentEmpty(slide.content)
					? slide.title || slide.description
						? {
								type: "doc",
								content: [
									...(slide.title || isBuilder
										? [
												{
													type: "heading",
													attrs: { level: 2 },
													content: [{ type: "text", text: slide.title || "Enter your title here" }],
												},
											]
										: []),
									...(slide.description || isBuilder
										? [
												{
													type: "paragraph",
													content: [
														{
															type: "text",
															text: slide.description || "Enter your description here...",
														},
													],
												},
											]
										: []),
								],
							}
						: (slide.content ?? null)
					: slide.content;

				return (
					<section
						key={idx}
						className={cn(
							`overflow-hidden flex items-center absolute inset-0 transition-opacity duration-1000`,
							currentSlide === idx ? "opacity-100" : "opacity-0 pointer-events-none",
							slide.boxAlignment === "left" && "justify-start",
							slide.boxAlignment === "center" && "justify-center",
							slide.boxAlignment === "right" && "justify-end",

							slide.textAlignment === "left" && "text-left",
							slide.textAlignment === "center" && "text-center",
							slide.textAlignment === "right" && "text-right",
							size === "full-width" && "items-stretch rounded-none",
							size === "fixed-width" && "rounded-lg",

							size === "fixed-width" && slide.alignmentVertical === "top" && "items-start",
							size === "fixed-width" && slide.alignmentVertical === "middle" && "items-center",
							size === "fixed-width" && slide.alignmentVertical === "bottom" && "items-end",
						)}
					>
						<div
							className={cn(
								"sm:w-1/2 space-y-4 text-(--theme-primary-color) p-8 lg:p-12 z-10 relative flex flex-col items-end justify-center",
								slide.textAlignment === "left" && "items-start",
								slide.textAlignment === "center" && "items-center",
								slide.textAlignment === "right" && "items-end",

								size === "full-width" && slide.alignmentVertical === "top" && "justify-start",
								size === "full-width" && slide.alignmentVertical === "middle" && "justify-center",
								size === "full-width" && slide.alignmentVertical === "bottom" && "justify-end",

								size === "fixed-width" && slide.boxAlignment === "left" && "rounded-r-sm",
								size === "fixed-width" && slide.boxAlignment === "center" && "rounded-sm",
								size === "fixed-width" && slide.boxAlignment === "right" && "rounded-l-sm",
							)}
							style={{
								backgroundColor: slide.theme?.backgroundColor || theme?.backgroundColor || undefined,
								color: slide.theme?.color || theme?.color || undefined,
							}}
						>
							<div
								className={cn(
									"prose prose-sm prose-theme",
									"lg:prose-base prose-h2:text-[1.5em] lg:prose-h2:text-4xl prose-h2:my-2 prose-h3:my-2 prose-h4:my-2 prose-p:my-1",
									"max-w-none! *:h-full w-full",
								)}
								data-yns-click
							>
								{isBuilder ? (
									<RichTextEditorLazy fieldPath={`slides.${idx}.content`} content={content} />
								) : isContentEmpty(content) ? null : (
									<div className="tiptap-result *:first:mt-0">{safeRenderToReactElement(content)}</div>
								)}
							</div>
							<div className="flex flex-row flex-wrap gap-2">
								{slide.button?.label && (
									<YnsLink
										className={cn(
											"inline-flex h-10 items-center justify-center px-6 font-medium text-(--theme-button-color) transition-colors",
											"focus:outline-hidden focus:ring-1 focus:ring-(--theme-button-color)",
											"bg-[var(--theme-hero-section-cta-background-color,#222)]",
											"text-[var(--theme-hero-section-cta-color,#EEE)]",
											"hover:bg-[var(--theme-hero-section-cta-hover-background-color,#555)]",
											"border-[color:var(--theme-hero-section-cta-button-border-color)]",
											slide.theme?.buttonRadius === "none" && "rounded-none",
											slide.theme?.buttonRadius === "sm" && "rounded-sm",
											slide.theme?.buttonRadius === "md" && "rounded-md",
											slide.theme?.buttonRadius === "lg" && "rounded-lg",
											slide.theme?.buttonRadius === "full" && "rounded-full",
											slide.theme?.buttonBorder === "none" && "border-none",
											slide.theme?.buttonBorder === "sm" && "border-1",
											slide.theme?.buttonBorder === "md" && "border-2",
											slide.theme?.buttonBorder === "lg" && "border-4",
										)}
										style={{
											"--theme-hero-section-cta-background-color":
												slide.theme?.buttonBackgroundColor || theme?.buttonBackgroundColor || undefined,
											"--theme-hero-section-cta-color":
												slide.theme?.buttonTextColor || theme?.buttonTextColor || undefined,
											"--theme-hero-section-cta-hover-background-color":
												slide.theme?.buttonHoverBackgroundColor ||
												theme?.buttonHoverBackgroundColor ||
												undefined,
											"--theme-hero-section-cta-button-border-color":
												slide.theme?.buttonBorderColor || undefined,
										}}
										href={slide.button.path ?? "#"}
									>
										{slide.button.label}
									</YnsLink>
								)}
								{slide.button2?.label && (
									<YnsLink
										className={cn(
											"inline-flex h-10 items-center justify-center rounded-full px-6 font-medium text-(--theme-button-color) transition-colors",
											"focus:outline-hidden focus:ring-1 focus:ring-(--theme-button-color)",
											"bg-[var(--theme-hero-section-cta-background-color,#222)]",
											"text-[var(--theme-hero-section-cta-color,#EEE)]",
											"hover:bg-[var(--theme-hero-section-cta-hover-background-color,#555)]",
											"border-[color:var(--theme-hero-section-cta-button-border-color)]",
											slide.theme?.buttonRadius === "none" && "rounded-none",
											slide.theme?.buttonRadius === "sm" && "rounded-sm",
											slide.theme?.buttonRadius === "md" && "rounded-md",
											slide.theme?.buttonRadius === "lg" && "rounded-lg",
											slide.theme?.buttonRadius === "full" && "rounded-full",
											slide.theme?.buttonBorder === "none" && "border-none",
											slide.theme?.buttonBorder === "sm" && "border-1",
											slide.theme?.buttonBorder === "md" && "border-2",
											slide.theme?.buttonBorder === "lg" && "border-4",
										)}
										style={{
											"--theme-hero-section-cta-background-color":
												slide.theme?.button2BackgroundColor ||
												slide.theme?.buttonBackgroundColor ||
												undefined,
											"--theme-hero-section-cta-color":
												slide.theme?.button2TextColor || slide.theme?.buttonTextColor || undefined,
											"--theme-hero-section-cta-hover-background-color":
												slide.theme?.button2HoverBackgroundColor ||
												slide.theme?.buttonHoverBackgroundColor ||
												undefined,
											"--theme-hero-section-cta-button-border-color":
												slide.theme?.button2BorderColor || undefined,
										}}
										href={slide.button2.path ?? "#"}
									>
										{slide.button2.label}
									</YnsLink>
								)}
							</div>
						</div>
						{slide.image?.src &&
							(() => {
								// @todo we should save it under slide.video instead so it's obvious we have a video
								const isVideo =
									slide.image.src.toLowerCase().endsWith(".mp4") ||
									slide.image.src.toLowerCase().endsWith(".webm") ||
									slide.image.src.toLowerCase().endsWith(".ogg");

								const objectPositionX = slide.objectPositionHorizontal ?? "center";
								const objectPositionY = slide.objectPositionVertical ?? "center";

								const className = tw`rounded absolute top-0 left-0 object-cover w-full h-full`;

								if (isVideo) {
									return (
										<video
											autoPlay
											loop
											muted
											playsInline
											className={className}
											style={{
												objectFit: "cover",
												zIndex: "1",
												objectPosition: `${objectPositionX} ${objectPositionY}`,
											}}
										>
											{/* @todo how do we make sure videos are optimized? */}
											<source src={slide.image.src} type="video/mp4" />
											{slide.image.alt}
										</video>
									);
								} else {
									return (
										<Image
											alt={slide.image.alt}
											loading="eager"
											priority={true}
											fetchPriority="high"
											className={className}
											width={1280}
											height={720}
											src={slide.image.src}
											style={{
												objectFit: "cover",
												zIndex: "1",
												objectPosition: `${objectPositionX} ${objectPositionY}`,
											}}
											sizes="(max-width: 1280px) 100vw, 1280px"
										/>
									);
								}
							})()}
					</section>
				);
			})}

			{/* Indicators */}

			{slides.length > 1 && (
				<div
					role="tablist"
					className="z-10 flex flex-row gap-2 absolute bottom-6 left-1/2 -translate-x-1/2 backdrop-blur-sm rounded-sm px-2 py-1"
					style={{
						"--time": `${TIME}ms`,
					}}
				>
					{slides.map((_slide, idx) => (
						// biome-ignore lint/a11y/useAriaPropsSupportedByRole: of course it is supported
						<button
							key={idx}
							type="button"
							className={cn(
								"h-1.5 w-2.5 rounded-full bg-neutral-200 cursor-pointer transform-gpu will-change-[width] transition-[width] duration-700",
								"before:content-[''] before:block before:rounded-full before:transform-gpu will-change-[width] before:transition-[width,opacity] before:duration-700 before:opacity-0 before:bg-black before:w-0 before:h-full",
								currentSlide === idx &&
									"w-12 transition-[width] before:w-full before:transition-[width,opacity] before:duration-[var(--time),700ms] before:ease-linear before:starting:w-0 before:starting:opacity-0 before:opacity-100",

								// fast transition after click
								currentSlide === idx && intervalRef.current === null && "before:duration-[300ms]",
							)}
							aria-selected={currentSlide === idx}
							aria-busy={Boolean(currentSlide === idx && intervalRef.current)}
							aria-controls={`testimonial-${idx}`}
							onClick={() => {
								if (intervalRef.current) {
									clearInterval(intervalRef.current);
									intervalRef.current = null;
								}
								setCurrentSlide(idx);
							}}
						>
							<span className="sr-only">Slide {idx + 1}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
};
